import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Eye, EyeOff, Loader2, Trash } from "lucide-react";
import { secretSelectors } from "./secretReducer";
import type { Secret, SecretState } from "./types";
import { validateSecretKey, validateSecretValue } from "./utils";

interface SecretRowProps {
  secret: Secret;
  index: number;
  state: SecretState;
  onUpdateSecret: (index: number, updates: Partial<Secret>) => void;
  onKeyDown: (e: React.KeyboardEvent, index: number) => void;
  onStartEditing: (secret: Secret, index: number) => void;
  onBlur: (index: number) => void;
  onToggleVisibility: (key: string) => void;
  onDeleteClick: (index: number, secret: Secret) => void;
  setInputRef: (refKey: string, element: HTMLInputElement | null) => void;
}

export function SecretRow({
  secret,
  index,
  state,
  onUpdateSecret,
  onKeyDown,
  onStartEditing,
  onBlur,
  onToggleVisibility,
  onDeleteClick,
  setInputRef,
}: SecretRowProps) {
  const isSecretEditing = secretSelectors.isEditing(state, secret.key);
  const isSecretVisible = secretSelectors.isVisible(state, secret.key);
  const isSecretLoading = secretSelectors.isLoading(state, secret.key);
  const secretError = secretSelectors.getError(state, secret.key);
  const isNewSecret = !secret.isExisting;

  const keyError = secret?.key
    ? validateSecretKey(
        secret.key,
        state.secrets.map((s) => s.key),
        index,
      )
    : null;
  const valueError = secret?.value ? validateSecretValue(secret.value) : null;

  return (
    <div key={secret.id || `fallback-${index}`} className="flex gap-2">
      <div className="flex gap-2 flex-1">
        <div className="w-1/2">
          <Input
            ref={(el) => setInputRef(`${index}-key`, el)}
            placeholder="API_KEY"
            value={secret?.key || ""}
            onChange={(e) => onUpdateSecret(index, { key: e.target.value })}
            onKeyDown={(e) => onKeyDown(e, index)}
            className={cn(
              "h-8 py-2 px-3 font-mono",
              keyError && "border-red-500",
              !isNewSecret && "bg-muted",
            )}
            readOnly={!isNewSecret}
            disabled={isSecretLoading}
          />
          {keyError && <p className="text-xs text-red-500 mt-1">{keyError}</p>}
        </div>
        <div className="w-1/2 relative">
          <Input
            ref={(el) => setInputRef(`${index}-value`, el)}
            placeholder={isSecretEditing ? "Enter new value" : "••••••••"}
            value={
              isNewSecret
                ? secret?.value || ""
                : isSecretEditing
                  ? secret?.value || ""
                  : "••••••••"
            }
            onChange={(e) => onUpdateSecret(index, { value: e.target.value })}
            onFocus={() => {
              if (!isNewSecret) {
                onStartEditing(secret, index);
              }
            }}
            onKeyDown={(e) => onKeyDown(e, index)}
            onBlur={() => onBlur(index)}
            className={cn(
              "h-8 py-2 px-3 font-mono pr-12",
              valueError && "border-red-500",
              !isNewSecret && !isSecretEditing && "text-muted-foreground",
              "transition-colors-none",
            )}
            type={
              isNewSecret || isSecretEditing
                ? isSecretVisible
                  ? "text"
                  : "password"
                : "text"
            }
            disabled={isSecretLoading}
          />
          {!isNewSecret && !isSecretEditing && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-6 px-2 text-xs"
              onClick={() => onStartEditing(secret, index)}
              disabled={isSecretLoading}
            >
              Edit
            </Button>
          )}
          {!isNewSecret && isSecretEditing && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-6 px-2"
              onClick={() => onToggleVisibility(secret.key)}
              onMouseDown={(e) => e.preventDefault()}
              disabled={isSecretLoading}
              aria-label={
                isSecretVisible ? "Hide secret value" : "Show secret value"
              }
            >
              {isSecretVisible ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
          )}
          {isNewSecret && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-6 px-2"
              onClick={() => onToggleVisibility(secret.key)}
              onMouseDown={(e) => e.preventDefault()}
              disabled={isSecretLoading}
              aria-label={
                isSecretVisible ? "Hide secret value" : "Show secret value"
              }
            >
              {isSecretVisible ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
          )}
          {valueError && (
            <p className="text-xs text-red-500 mt-1">{valueError}</p>
          )}
          {secretError && (
            <p className="text-xs text-red-500 mt-1">{secretError}</p>
          )}
        </div>
      </div>
      <div className="w-12 flex justify-center">
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-8 w-8"
          onClick={(e) => {
            e.stopPropagation();
            onDeleteClick(index, secret);
          }}
          aria-label="Delete secret"
          disabled={isSecretLoading}
        >
          {isSecretLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
}
